import { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, Alert, Typography, Empty } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ShoppingCartOutlined, DollarOutlined, CalendarOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { crmApi } from '../../../api/client';
import { formatValue } from '../../../utils/chartFormatters';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface ContractorAnalysisProps {
    contractorId: string;
}

interface AnalysisData {
    stats: {
        revenue12m: number;
        ordersCount: number;
        aov: number;
        daysSinceLastOrder: number;
    } | null;
    revenueChart: { month: string; revenue: number }[];
    topProducts: { name: string; sku: string; revenue: number; quantity: number }[];
}

export const ContractorAnalysis = ({ contractorId }: ContractorAnalysisProps) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<AnalysisData>({
        stats: null,
        revenueChart: [],
        topProducts: []
    });
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [statsRes, revenueRes, productsRes] = await Promise.all([
                    crmApi.getContractorStats(contractorId),
                    crmApi.getMonthlyRevenue(contractorId),
                    crmApi.getTopProducts(contractorId)
                ]);

                setData({
                    stats: statsRes.data,
                    revenueChart: revenueRes.data,
                    topProducts: productsRes.data
                });
            } catch (err) {
                console.error("Failed to fetch analysis data", err);
                setError("Nie udało się pobrać danych analitycznych.");
            } finally {
                setLoading(false);
            }
        };

        if (contractorId) {
            fetchData();
        }
    }, [contractorId]);

    if (loading) return <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>;
    if (error) return <Alert message={error} type="error" />;

    const columns = [
        {
            title: 'Produkt',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => (
                <div>
                    <Text strong>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.sku}</Text>
                </div>
            )
        },
        {
            title: 'Ilość',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right' as const,
        },
        {
            title: 'Przychód',
            dataIndex: 'revenue',
            key: 'revenue',
            align: 'right' as const,
            render: (val: number) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(val)
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            {/* KPIs */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Przychód (12 mcy)"
                            value={data.stats?.revenue12m}
                            precision={2}
                            prefix={<DollarOutlined />}
                            suffix="PLN"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Liczba Zamówień"
                            value={data.stats?.ordersCount}
                            prefix={<ShoppingCartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Śr. Wartość Koszyka (AOV)"
                            value={data.stats?.aov}
                            precision={2}
                            suffix="PLN"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title="Dni od ost. zamówienia"
                            value={data.stats?.daysSinceLastOrder === -1 ? '-' : data.stats?.daysSinceLastOrder}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: (data.stats?.daysSinceLastOrder || 0) > 60 ? '#cf1322' : undefined }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Chart */}
                <Col xs={24} lg={14}>
                    <Card title="Przychód miesięczny (Ostatnie 12 miesięcy)" bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div style={{ height: 350 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    data={data.revenueChart}
                                    margin={{
                                        top: 10,
                                        right: 30,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="month" stroke="#8c8c8c" fontSize={12} tickLine={false} />
                                    <YAxis stroke="#8c8c8c" fontSize={12} tickLine={false} tickFormatter={formatValue} />
                                    <Tooltip
                                        formatter={(value: number) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value)}
                                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                                    />
                                    <Area type="monotone" dataKey="revenue" stroke="#1890ff" fill="#e6f7ff" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>

                {/* Top Products */}
                <Col xs={24} lg={10}>
                    <Card title="Top 5 Produktów (Przychód)" bordered={false} style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Table
                            dataSource={data.topProducts}
                            columns={columns}
                            rowKey="sku"
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
